import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserJob } from '@/lib/types';
import { useIsMobile } from '@/hooks/use-mobile';
import { debug } from '@/lib/utils';

// Different levels of disclosure
export type DisclosureLevel = 'minimal' | 'preview' | 'detailed';

// Define interface for user preferences
interface UserPreferences {
  defaultView: DisclosureLevel;
  autoExpandOnQuote: boolean;
  autoExpandOnSearch: boolean;
  rememberExpandedJobs: boolean;
}

// Define context shape
interface ProgressiveDisclosureContextType {
  // Get the disclosure level for a specific job
  getJobDisclosureLevel: (jobId: string) => DisclosureLevel;

  // Get the disclosure level for a new job that's not seen before
  getDefaultDisclosureLevel: () => DisclosureLevel;

  // Update a job's disclosure level
  setJobDisclosureLevel: (jobId: string, level: DisclosureLevel) => void;

  // Get user preferences
  preferences: UserPreferences;

  // Update user preferences
  updatePreferences: (prefs: Partial<UserPreferences>) => void;

  // Check if a job should be auto-expanded based on context
  shouldAutoExpand: (job: UserJob, context?: { hasQuotes?: boolean, isHighlighted?: boolean }) => boolean;

  // Get jobs that should be featured prominently
  getFeaturedJobs: (jobs: UserJob[]) => string[];
}

// Create the context
const ProgressiveDisclosureContext = createContext<ProgressiveDisclosureContextType | undefined>(undefined);

// Create the provider component
export function ProgressiveDisclosureProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();

  // Default user preferences based on device
  const defaultPreferences: UserPreferences = {
    defaultView: 'minimal', // Start with minimal view for all devices
    autoExpandOnQuote: false,
    autoExpandOnSearch: true,
    rememberExpandedJobs: true,
  };

  // State for user preferences
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);

  // State for job disclosure levels
  const [jobLevels, setJobLevels] = useState<Record<string, DisclosureLevel>>({});

  // Load preferences and saved states from localStorage on mount
  useEffect(() => {
    try {
      const savedPrefs = localStorage.getItem('job-disclosure-preferences');
      if (savedPrefs) {
        setPreferences(prev => ({ ...prev, ...JSON.parse(savedPrefs) }));
      }

      const savedLevels = localStorage.getItem('job-disclosure-levels');
      if (savedLevels && preferences.rememberExpandedJobs) {
        setJobLevels(JSON.parse(savedLevels));
      }
    } catch (error) {
      debug.error('Error loading progressive disclosure settings:', error);
    }
  }, []);

  // Save preferences and levels to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('job-disclosure-preferences', JSON.stringify(preferences));

      if (preferences.rememberExpandedJobs) {
        localStorage.setItem('job-disclosure-levels', JSON.stringify(jobLevels));
      }
    } catch (error) {
      debug.error('Error saving progressive disclosure settings:', error);
    }
  }, [preferences, jobLevels]);

  // Get default disclosure level based on preferences and device
  const getDefaultDisclosureLevel = (): DisclosureLevel => {
    return preferences.defaultView;
  };

  // Get disclosure level for a specific job
  const getJobDisclosureLevel = (jobId: string): DisclosureLevel => {
    return jobLevels[jobId] || getDefaultDisclosureLevel();
  };

  // Update a job's disclosure level
  const setJobDisclosureLevel = (jobId: string, level: DisclosureLevel) => {
    setJobLevels(prev => ({
      ...prev,
      [jobId]: level,
    }));
  };

  // Update user preferences
  const updatePreferences = (prefs: Partial<UserPreferences>) => {
    setPreferences(prev => ({ ...prev, ...prefs }));

    // If rememberExpandedJobs is turned off, clear saved levels
    if (prefs.rememberExpandedJobs === false) {
      localStorage.removeItem('job-disclosure-levels');
    }
  };

  // Determine if a job should be auto-expanded based on context and preferences
  const shouldAutoExpand = (
    job: UserJob,
    context?: { hasQuotes?: boolean, isHighlighted?: boolean }
  ): boolean => {
    // If the job is highlighted (e.g., from search), auto-expand it
    if (context?.isHighlighted && preferences.autoExpandOnSearch) {
      return true;
    }

    // If the job has quotes and preference is set, auto-expand it
    if (context?.hasQuotes && preferences.autoExpandOnQuote) {
      return true;
    }

    // If the job is awaiting reply or has high priority status, expand it
    if (job.status === 'awaiting_reply') {
      return true;
    }

    return false;
  };

  // Get jobs that should be featured (shown more prominently)
  const getFeaturedJobs = (jobs: UserJob[]): string[] => {
    return jobs
      .filter(job =>
        job.status === 'awaiting_reply' ||
        job.status === 'quote_requested'
      )
      .map(job => job.id);
  };

  // Create the context value
  const contextValue: ProgressiveDisclosureContextType = {
    getJobDisclosureLevel,
    getDefaultDisclosureLevel,
    setJobDisclosureLevel,
    preferences,
    updatePreferences,
    shouldAutoExpand,
    getFeaturedJobs,
  };

  return (
    <ProgressiveDisclosureContext.Provider value={contextValue}>
      {children}
    </ProgressiveDisclosureContext.Provider>
  );
}

// Custom hook to use the context
export function useProgressiveDisclosure() {
  const context = useContext(ProgressiveDisclosureContext);

  if (context === undefined) {
    throw new Error('useProgressiveDisclosure must be used within a ProgressiveDisclosureProvider');
  }

  return context;
}
